import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { Grid, Search } from "@bryntum/schedulerpro";
import { useEffect, useState, useMemo, useCallback } from "react";
import { debounce } from "@/other/utils/otherUtils";

export const useSwivuAppSearch = () => {
  const { viewManager } = useSwivuBryntumContext();

  const [activeSearchGrid, setActiveSearchGrid] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");
  const [foundCount, setFoundCount] = useState(0);
  const [pageViewsRef, setPageViewsRef] = useState<Record<string, Grid> | null>(null);

  const searchableGrids = useMemo(() => {
    if (!pageViewsRef) return [];
    return Object.entries(pageViewsRef).reduce<Array<{ id: string; grid: Grid }>>((acc, [id, grid]) => {
      if ((grid as Grid)?.features?.search) {
        acc.push({ id, grid: grid as Grid });
      }
      return acc;
    }, []);
  }, [pageViewsRef]);

  const hasMultipleGrids = searchableGrids.length > 1;

  // Set default active grid
  useEffect(() => {
    if (!activeSearchGrid && searchableGrids.length > 0) {
      setActiveSearchGrid(searchableGrids[0].id);
    }
  }, [searchableGrids, activeSearchGrid]);

  // Get current searcher from active grid
  const getCurrentSearcher = useCallback((): Search | undefined => {
    if (!activeSearchGrid || !pageViewsRef) return undefined;
    const grid = pageViewsRef[activeSearchGrid];
    return grid?.features?.search;
  }, [activeSearchGrid, pageViewsRef]);


  /* TODO: This is a temporary solution to open all collapsed groups when a search is performed.
  We are trying to fix this bryntum issue: https://github.com/bryntum/support/issues/12687
  Once the issue is fixed, we can remove this function & its usage in this hook.
  */
  const openCollapsedGroups = useCallback(async (searcher: Search, newActiveGrid?: Grid) => {
    const activeGrid = newActiveGrid || searchableGrids.find(({ id }) => id === activeSearchGrid)?.grid;
    if (searcher.foundCount > 0 && activeGrid && activeGrid.features.group) {
      /* We want to expand all groups, await this as it returns a promise */
      await activeGrid.features.group.expandAll();
      /* Scroll the first hit into view */
      searcher.gotoFirstHit();
    }
  }, [activeSearchGrid, searchableGrids]);

  // Create debounced search function
  const debouncedSearch = useCallback(debounce(async (text: string, searcher: Search) => {
    await searcher.search(text);
    openCollapsedGroups(searcher);
    setFoundCount(searcher.foundCount ?? 0);
  }, 700), [openCollapsedGroups]);

  const search = useCallback(
    (text: string): void => {
      setSearchText(text);

      const searcher = getCurrentSearcher();
      if (!searcher) return;

      if (!text.trim()) {
        searcher.clear();
        setFoundCount(0);
        return;
      }

      debouncedSearch(text, searcher);
    },
    [getCurrentSearcher, debouncedSearch]
  );

  const clear = useCallback(() => {
    setSearchText("");
    const searcher = getCurrentSearcher();
    if (searcher) {
      searcher.clear();
    }
    setFoundCount(0);
  }, [getCurrentSearcher]);

  const reset = useCallback(() => {
    // Clear all Bryntum searches
    searchableGrids.forEach(({ grid }) => {
      grid.features?.search?.clear();
    });

    // Reset all state
    setSearchText("");
    setFoundCount(0);
    setActiveSearchGrid(null);
  }, [searchableGrids]);

  const setActiveGrid = useCallback(
    async (gridId: string) => {
      if (!pageViewsRef?.[gridId]?.features?.search) return;

      const searcher = getCurrentSearcher();
      if (searcher) {
        searcher.clear();
        setFoundCount(0);
      }
      setActiveSearchGrid(gridId);

      // Re-run search on new grid if there's active search text
      if (searchText.trim()) {
        const newSearcher = pageViewsRef[gridId].features.search;
        await newSearcher.search(searchText);
        /* Pass the new active grid to the openCollapsedGroups function, as the state is async and we need to ensure the groups are open if any records are found */
        openCollapsedGroups(newSearcher, pageViewsRef[gridId]);
        setFoundCount(newSearcher.foundCount ?? 0);
      }
    }, [pageViewsRef, searchText, getCurrentSearcher, openCollapsedGroups]
  );

  const gotoNextHit = useCallback(() => {
    const searcher = getCurrentSearcher();
    if (searcher) {
      searcher.gotoNextHit();
    }
  }, [getCurrentSearcher]);

  const gotoPrevHit = useCallback(() => {
    const searcher = getCurrentSearcher();
    if (searcher) {
      searcher.gotoPrevHit();
    }
  }, [getCurrentSearcher]);

  // This useEffect will run as soon as new page views are loaded by the `useEnableViewTracking` hook, cleanup function resets all search state when page views change
  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  // Subscribe to page views changes
  useEffect(() => {
    if (!viewManager) return;

    const detacher = viewManager.addListenerForPageViewsRef((value: Record<string, Grid>) => {
      setPageViewsRef(value);
    });

    return detacher;
  }, [viewManager]);

  const isSearchable = searchableGrids.length > 0;

  return {
    searchText,
    searchCount: foundCount,
    activeGrid: activeSearchGrid,
    isSearchable,
    hasMultipleGrids,
    searchableGrids,
    search,
    clear,
    setActiveGrid,
    gotoNextHit,
    gotoPrevHit
  };
};
